import { Button, Card, ToggleButton, ToggleButtonGroup } from '@mui/material';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
	type InvoiceMonthlyDetailParameters,
	InvoiceParameters,
	InvoiceRateTypes,
	type InvoiceReportFormat,
	type SubscriberType,
} from '@parkchirp/reporting';
import { useMemo, useState } from 'react';
import { AccountFilter, AccountFilterValue } from './AccountFilter';
import { FacilityHierarchyFilter, FacilityHierarchyFilterValue } from './FacilityHierarchyFilter';
import { applyFacilityHierarchyToParameters } from './facilityHierarchyParameters';
import { InvoiceDateFilter, InvoiceDateFilterValue } from './InvoiceDateFilter';
import { InvoiceRateTypesFilter } from './InvoiceRateTypesFilter';
import { SubscriberTypeFilter } from './SubscriberTypeFilter';
import { ReportFormProps } from './types';

const initialDateFilter: InvoiceDateFilterValue = {
	invoiceStartDate: null,
	invoiceEndDate: null,
	parkingPeriodStartMonth: '',
	parkingPeriodStartYear: '',
	parkingPeriodEndMonth: '',
	parkingPeriodEndYear: '',
};

const initialFacilityHierarchy: FacilityHierarchyFilterValue = {
	areas: [],
	branches: [],
	districts: [],
	facilities: [],
};

const defaultRateTypes: InvoiceRateTypes[] = ['Monthly', 'Validation'];

const hasParkingPeriodStart = (dateFilter: InvoiceDateFilterValue) => {
	return dateFilter.parkingPeriodStartMonth !== '' && dateFilter.parkingPeriodStartYear !== '';
};

export const InvoiceDetailReportForm = ({
	createReport,
	isGeneratingReport,
	organizationId,
	onValidationError,
}: ReportFormProps) => {
	const [dateFilter, setDateFilter] = useState<InvoiceDateFilterValue>(initialDateFilter);
	const [accountFilter, setAccountFilter] = useState<AccountFilterValue>({ type: 'all' });
	const [rateTypes, setRateTypes] = useState<InvoiceRateTypes[]>(defaultRateTypes);
	const [subscriberType, setSubscriberType] = useState<SubscriberType>('All');
	const [facilityHierarchy, setFacilityHierarchy] = useState<FacilityHierarchyFilterValue>(initialFacilityHierarchy);
	const [format, setFormat] = useState<InvoiceReportFormat>('csv');

	const canSubmit = useMemo(() => {
		if (rateTypes.length === 0) {
			return false;
		}

		return Boolean(dateFilter.invoiceStartDate) || hasParkingPeriodStart(dateFilter);
	}, [dateFilter, rateTypes]);

	const reset = () => {
		setDateFilter(initialDateFilter);
		setAccountFilter({ type: 'all' });
		setRateTypes(defaultRateTypes);
		setSubscriberType('All');
		setFacilityHierarchy(initialFacilityHierarchy);
		setFormat('csv');
	};

	const buildAccountFilter = (): InvoiceMonthlyDetailParameters['accountFilter'] => {
		switch (accountFilter.type) {
			case 'accountId':
				return {
					type: 'accountId',
					value: Number(accountFilter.value),
				};

			case 'subscriptionName':
				return {
					type: 'subscriptionName',
					value: accountFilter.value.trim(),
				};
			default:
				return undefined;
		}
	};

	const buildParameters = (): InvoiceParameters => {
		const parameters: InvoiceParameters = {
			rateType: rateTypes,
			payorType: subscriberType,
			format,
		};

		if (dateFilter.invoiceStartDate) {
			parameters.invoiceStartDate = dateFilter.invoiceStartDate.format('YYYY-MM-DD');

			if (dateFilter.invoiceEndDate) {
				parameters.invoiceEndDate = dateFilter.invoiceEndDate.format('YYYY-MM-DD');
			}
		}

		if (hasParkingPeriodStart(dateFilter)) {
			if (dateFilter.parkingPeriodStartMonth !== '' && dateFilter.parkingPeriodStartYear !== '') {
				parameters.parkingPeriodStartMonth = dateFilter.parkingPeriodStartMonth;
				parameters.parkingPeriodStartYear = dateFilter.parkingPeriodStartYear;
			}

			if (dateFilter.parkingPeriodEndMonth !== '' && dateFilter.parkingPeriodEndYear !== '') {
				parameters.parkingPeriodEndMonth = dateFilter.parkingPeriodEndMonth;
				parameters.parkingPeriodEndYear = dateFilter.parkingPeriodEndYear;
			}
		}

		const accountFilterValue = buildAccountFilter();

		if (accountFilterValue) {
			parameters.accountFilter = accountFilterValue;
		}

		applyFacilityHierarchyToParameters(parameters, facilityHierarchy);

		if (facilityHierarchy.facilities.length === 1) {
			const facilityNumber = facilityHierarchy.facilities[0].facilityNumber;
			if (facilityNumber !== undefined && facilityNumber !== null && `${facilityNumber}` !== '') {
				parameters.facilityNumber = facilityNumber;
			}
		}

		return parameters;
	};

	const validate = () => {
		if (rateTypes.length === 0) {
			onValidationError('Select at least one rate type.');
			return false;
		}

		if (!dateFilter.invoiceStartDate && !hasParkingPeriodStart(dateFilter)) {
			onValidationError('Select an invoice start date or a parking period start month and year.');
			return false;
		}

		if (
			(dateFilter.parkingPeriodEndMonth !== '' || dateFilter.parkingPeriodEndYear !== '') &&
			!hasParkingPeriodStart(dateFilter)
		) {
			onValidationError('Select a parking period start before specifying a parking period end.');
			return false;
		}

		if (accountFilter.type === 'accountId' && !accountFilter.value) {
			onValidationError('Enter an account ID.');
			return false;
		}

		if (accountFilter.type === 'subscriptionName' && !accountFilter.value.trim()) {
			onValidationError('Enter a subscription name.');
			return false;
		}

		return true;
	};

	const submit = async () => {
		if (!validate()) {
			return;
		}

		await createReport({
			reportType: 'invoice',
			parameters: buildParameters(),
		});
	};

	return (
		<Card variant='outlined' sx={{ p: 3 }}>
			<Stack spacing={3}>
				<InvoiceDateFilter value={dateFilter} onChange={setDateFilter} />

				<Divider />

				<AccountFilter filter={accountFilter} onChange={setAccountFilter} />

				<Divider />

				<SubscriberTypeFilter value={subscriberType} onChange={setSubscriberType} />

				<Divider />

				<InvoiceRateTypesFilter selectedRateTypes={rateTypes} onChange={setRateTypes} />

				<Divider />

				<FacilityHierarchyFilter
					organizationId={organizationId}
					value={facilityHierarchy}
					onChange={setFacilityHierarchy}
				/>

				<Divider />

				<Stack spacing={1.5}>
					<Typography variant='subtitle1' fontWeight={600}>
						Output
					</Typography>

					<ToggleButtonGroup
						exclusive
						size='small'
						value={format}
						onChange={(_, nextFormat: InvoiceReportFormat | null) => {
							if (nextFormat) {
								setFormat(nextFormat);
							}
						}}
					>
						<ToggleButton value='csv'>CSV</ToggleButton>
						<ToggleButton value='pdf'>PDF</ToggleButton>
						<ToggleButton value='xlsx'>XLSX</ToggleButton>
					</ToggleButtonGroup>
				</Stack>

				<Divider />

				<Stack direction='row' justifyContent='flex-end' spacing={2}>
					<Button onClick={reset} disabled={isGeneratingReport}>
						Reset
					</Button>

					<Button onClick={submit} disabled={!canSubmit || isGeneratingReport}>
						Generate Report
					</Button>
				</Stack>
			</Stack>
		</Card>
	);
};
